import {
  AfterViewInit,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  model,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {StockService} from '../../../shared/services/stock.service';
import {ContextService} from '../../../shared/services/context.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {StockArrival} from '../../../shared/models/stockArrival';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatFooterCell,
  MatFooterRow,
  MatFooterRowDef,
  MatHeaderCell,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource
} from '@angular/material/table';
import {Alert, AlertService, AlertType, MainContentModule} from '@michelin/theme';
import {MatInput} from '@angular/material/input';
import {MatSort, MatSortHeader} from '@angular/material/sort';
import {NoDataComponent} from '../../../shared/components/no-data/no-data.component';
import {NoScrollDirective} from '../../../directives/nx-no-scroll.directive';
import {FormArray, FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {YearScrollerComponent} from '../../fleet/year-scroller/year-scroller.component';
import {IModif} from '../../../shared/models/IModif';
import {NgxMaskDirective, provideNgxMask} from 'ngx-mask';
import {COL_CAI, COL_SEAT, COL_TIRE_SPEC, MAX_VALUE} from '../../../shared/constants/StockConstants';
import moment from 'moment';
import {NavigationConfirmService} from "../../../shared/services/navigation-confirm.service";

@Component({
  selector: 'mic-stock-arrivals-table',
  imports: [
    TranslatePipe,
    MainContentModule,
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatFooterCell,
    MatFooterRow,
    MatFooterRowDef,
    MatHeaderCell,
    MatHeaderRow,
    MatHeaderRowDef,
    MatInput,
    MatRow,
    MatRowDef,
    MatSort,
    MatSortHeader,
    MatTable,
    NoDataComponent,
    NoScrollDirective,
    YearScrollerComponent,
    NgxMaskDirective
  ],
  templateUrl: './arrivals-table.component.html',
  styleUrl: './arrivals-table.component.scss',
  providers: [provideNgxMask()]
})
export class ArrivalsTableComponent implements OnInit, AfterViewInit, OnChanges, IModif, OnDestroy {
  siteId: number;
  readonly digitMask = '0*';
  readonly digitPatterns = { '0': { pattern: /\d/ } };

  /**
   * Table config properties
   */
  currentMonth = new Date().getMonth() + 1;
  monthsNumbersToDisplay = Array.from(
    { length: 12 },
    (_, i) =>
      moment()
        .month(this.currentMonth - 1)
        .add(i, 'months')
        .month() + 1
  );
  @ViewChild(MatSort) sort!: MatSort;
  dataSource = new MatTableDataSource<StockArrival>();
  columns = [COL_SEAT, COL_TIRE_SPEC, COL_CAI, ...this.monthsNumbersToDisplay.map((m) => m.toString())];

  /**
   * Properties to manage component state and interactions with parent component
   */
  isLoadingData = model<boolean>();
  isSavingData = model<boolean>();
  isModificationsPending = model<boolean>(false);
  startYear = model<number>(new Date().getFullYear());
  startMonth = model<number>(new Date().getMonth() + 1);
  @Input() tireSpecFilteredId: number;
  @Input() manufacturerFiltered: string[] = [];
  @Output() dataLoaded = new EventEmitter<StockArrival[]>();

  /**
   * Form array to manage input controls for stock quantities
   */
  formArray: FormArray;
  arrivalsControls: FormGroup[] = [];

  private isRollingBackSearchParams = false;

  constructor(
    private readonly stockService: StockService,
    private contextService: ContextService,
    private destroyRef: DestroyRef,
    private fb: FormBuilder,
    private alertService: AlertService,
    private translate: TranslateService,
    private navigationConfirm: NavigationConfirmService
  ) {
    this.contextService.register(this);
  }

  ngOnInit() {
    // Set up custom sorting for the table, as some columns are based on nested properties of the data source
    this.dataSource.sortingDataAccessor = (data, col) => {
      if (col === COL_SEAT) {
        return data.tireSpec.seat;
      } else if (col === COL_TIRE_SPEC) {
        return data.tireSpec.specification;
      } else if (col === COL_CAI) {
        return data.tireSpec.cai;
      }
    };
    // Load initial data and set up subscription to site changes
    this.contextService.siteId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((id) => {
      if (id !== null && id !== this.siteId) {
        this.siteId = id;
        this.loadStockArrivals();
      }
    });
  }

  ngAfterViewInit() {
    // Filter predicate to handle both manufacturer filtering and tire specification filtering. If the filter is empty, all data is shown.
    this.dataSource.filterPredicate = (data: StockArrival, filter: string) => {
      if (!filter) return true;
      try {
        const manufacturers: string[] = JSON.parse(filter);
        return manufacturers.length === 0 || manufacturers.includes(data.tireSpec.manufacturer);
      } catch {
        // Fallback to tire spec filtering if filter is not a JSON array (manufacturer filter)
        return data.tireSpec.specification.toLowerCase().includes(filter.toLowerCase());
      }
    };
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.startYear && !changes.startYear.firstChange) {
      if (this.isRollingBackSearchParams) {
        this.isRollingBackSearchParams = false;
        return;
      }

      const proceed = () => {
        this.loadStockArrivals();
      };
      const rollback = () => {
        this.isRollingBackSearchParams = true;
        this.startYear.update(() => changes.startYear.previousValue);
      };

      if (this.isModificationsPending()) {
        this.navigationConfirm.confirmNavigation(proceed, rollback);
      } else {
        proceed();
      }
    }

    if (changes.tireSpecFilteredId) {
      let tireSpecFiltered = this.dataSource.data
        .map((data) => data.tireSpec)
        .filter((tireSpec) => tireSpec.tireSpecId === this.tireSpecFilteredId)
        .map((tireSpec) => tireSpec.specification)[0];
      this.dataSource.filter = tireSpecFiltered?.trim().toLowerCase();
    }

    // If tireSpec filter also changed, manufacturer filter will be applied through the combinedLatest in parent component
    if (this.tireSpecFilteredId === undefined && changes.manufacturerFiltered) {
      this.applyManufacturerFilter();
    }
  }

  ngOnDestroy(): void {
    this.contextService.unRegister(this);
  }

  getModifStatus(): boolean {
    return this.isModificationsPending();
  }

  public loadStockArrivals() {
    if (!this.startYear() || !this.startMonth() || !this.siteId) return; // Ensure search params are defined, otherwise skip loading
    this.isLoadingData.set(true);
    this.dataSource.data = [] as StockArrival[];
    this.stockService
      .getStockArrivals(this.siteId, this.startYear(), this.startMonth())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((arrivals: StockArrival[]) => {
        this.dataSource.data = arrivals;
        this.dataLoaded.emit(arrivals);
        this.isLoadingData.set(false);
        // Reset modifications pending flag, as we just loaded fresh data
        this.isModificationsPending.update(() => false);
        this.dataSource.sort = this.sort;
        this.buildForm(); // (Re)build the form with the new data
      });
  }

  /**
   * Apply manufacturer filter to the data source. If no manufacturers are selected, clear the filter to show all data.
   * @private
   */
  private applyManufacturerFilter() {
    if (!this.manufacturerFiltered || this.manufacturerFiltered.length === 0) {
      this.dataSource.filter = ''; // No filter applied
    } else {
      // Encoding filter for filterPredicate
      this.dataSource.filter = JSON.stringify(this.manufacturerFiltered);
    }
  }

  /**
   * Compute the year for a given month number based on the current year and displayed months
   * @param month Month number (1-12)
   * @returns Corresponding year
   */
  getYearForMonth(month: number) {
    let index = this.monthsNumbersToDisplay.findIndex((m) => m === month);
    let year = this.startYear();
    for (let i = 0; i < index; i++) {
      // If bigger month number found before, it means we passed to next year
      if (this.monthsNumbersToDisplay[i] > month) {
        return year + 1;
      }
    }
    return year;
  }

  /**
   * Get the first day of a month in YYYY-MM-DD format
   * @param monthNumber Month number (1-12)
   * @returns String in format 'YYYY-MM-01'
   */
  getFirstDayOfMonth(monthNumber: number) {
    return moment({ year: this.getYearForMonth(monthNumber), month: monthNumber - 1, day: 1 }).format('YYYY-MM-DD');
  }

  /**
   * Get the FormControl for a specific cell based on row index and tire specification ID. This allows us to bind the input fields in the template to the correct controls in the form array.
   * @param tireSpecId ID of the tire specification for the column
   * @param month Month number for the column
   * @returns FormControl for the specified cell, or null if not found
   * @protected
   */
  protected getQuantityControl(tireSpecId: number, month: number): FormControl | null {
    let key: string = tireSpecId.toString() + '-' + month.toString();
    const group = this.arrivalsControls?.find((fg) => fg.contains(key));
    return group ? (group.get(key) as FormControl) : null;
  }

  /**
   * Build the form array based on the current data source. Each row corresponds to a tire specification, and each month column corresponds to a FormControl for the stock quantity. The form is rebuilt every time new data is loaded to ensure it reflects the latest state of the inventory.
   * @private
   */
  private buildForm(): void {
    this.formArray = this.fb.array(
      this.dataSource.data.map((element: StockArrival) => {
        const controls = this.monthsNumbersToDisplay.reduce((acc: { [key: string]: FormControl }, month: number) => {
          const key = element.tireSpec.tireSpecId.toString() + '-' + month.toString();
          acc[key] = new FormControl(element.ongoingDeliveryByMonth?.[this.getFirstDayOfMonth(month)]?.quantity ?? 0);
          return acc;
        }, {} as { [key: string]: FormControl });

        return this.fb.group(controls);
      })
    ) as FormArray<FormGroup>;
    this.arrivalsControls = this.formArray.controls as FormGroup[];
  }

  protected updateColumns(element: StockArrival, monthNumber: number, control: FormControl | null): void {
    const key = this.getFirstDayOfMonth(monthNumber);
    if (!element.ongoingDeliveryByMonth) {
      element.ongoingDeliveryByMonth = {};
    }

    if (!control.value) {
      control.setValue(0, { emitEvent: false });
    } else if (control.value > MAX_VALUE) {
      control.setValue(MAX_VALUE, { emitEvent: false });
    }

    const existing = element.ongoingDeliveryByMonth[key];

    element.ongoingDeliveryByMonth[key] = {
      ongoingDeliveryId: existing?.ongoingDeliveryId,
      ...(existing || {}),
      quantity: control.value,
      modified: false,
      deliveryDate: existing?.deliveryDate ?? key
    } ;

    this.isModificationsPending.set(true);
  }

  saveModifications() {
    this.isSavingData.set(true);
    this.stockService
      .saveStockArrivals(this.siteId, this.dataSource.data)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isModificationsPending.set(false);
          this.loadStockArrivals(); // Reload data after saving to get updated values from backend and reset form state
          this.isSavingData.set(false);
          this.alertService.showAlert({
            type: AlertType.success,
            title: `${this.translate.instant('stock.table.arrivalsTable.save.successTitle')}`
          } as Alert);
        },
        error: (err) => {
          let errorMsg: string | undefined = undefined;
          if (err.error && err.error.message) {
            errorMsg = `(${err.error.message})`;
          }
          this.isSavingData.set(false);
          this.alertService.showAlert({
            type: AlertType.critical,
            title: `${this.translate.instant('stock.table.arrivalsTable.save.errorTitle')}`,
            content: `${this.translate.instant('stock.table.arrivalsTable.save.errorMsg')} ${errorMsg}`
          } as Alert);
        }
      });
  }

  resetModifications() {
    this.loadStockArrivals();
  }

  /**
   * Calculate the total stock quantity for a given month
   * @param monthNumber Month number (1-12)
   * @returns Total stock quantity for that month
   */
  getTotalForMonth(monthNumber: number): number {
    const key = this.getFirstDayOfMonth(monthNumber);
    return this.dataSource.data.reduce((sum, element) => {
      const stock = element.ongoingDeliveryByMonth?.[key];
      if (!stock) return sum; // If no stock for this month, we continue to sum without adding anything
      return sum + (Number(stock.quantity) || 0);
    }, 0);
  }

  /**
   * Method to determine if user can select previous year in year scroller, based on selected start year and current year
   */
  protected canSelectPreviousYear() {
    return this.startYear() > new Date().getFullYear();
  }

  protected getMonthClass(frozenZone: number, month: number) {
    // Only apply frozen class if we are displaying months of the current year
    if (this.startYear() == new Date().getFullYear()) {
      const startIndex = this.monthsNumbersToDisplay.indexOf(this.currentMonth);
      const monthIndex = this.monthsNumbersToDisplay.indexOf(month);
      if (monthIndex >= startIndex && monthIndex < startIndex + frozenZone) {
        return 'frozen';
      }
    }
    return '';
  }
}
